import { HttpClient } from "@angular/common/http";
import { catchError, finalize, Observable, of, Subject, switchMap, throwError } from "rxjs";
import { ConfigService } from "../config.service";
import { SessionStorageService } from "../storage/session-storage.service";
import { BroadcastService } from "../services/broadcast.service";


export interface CacheObject<T> {
    data: T;
    timestamp: Date;
}

export abstract class CachingApiService<T> {

    //FUTURE: Update to use session storage to share across tabs.  
    private cache!: CacheObject<T>;

    /**Cache duration, in milliseconds.  Defaults to 5 minutes */
    protected cacheDuration: number = 5 * 60 * 1000; // 5 minutes
    
    protected abstract broadcastKey: string;

    private reloadInProgress: boolean = false;
    private reloadComplete: Subject<CacheObject<T>> = new Subject<CacheObject<T>>();

    private reloadGuard: number = 0

    constructor(
        protected http: HttpClient,
        protected config: ConfigService,
        protected storage: SessionStorageService,
        private broadcastService: BroadcastService
    ) {

        this.broadcastService.onMessage$.subscribe((message) => {
            if (message.type === 'caching-api-service-reload' && message.data.key === this.broadcastKey) {
                this.cache = message.data.data
            }
        })


    }

    init() {
        return new Promise<void>((resolve, reject) => {
            this.getCache().subscribe(
                {
                    next: () => {
                        resolve();
                    },
                    error: (err) => {
                        reject(err);
                    }
                })
        })
    }


    protected getCache(): Observable<CacheObject<T>> {
        if (this.reloadInProgress) {
            return this.reloadComplete
        }
        else {
            if (!this.cache || this.cache.timestamp.valueOf() < new Date().getTime() - this.cacheDuration) {
                return this.reloadData();
            }
            else {
                return of({ ...this.cache });
            }
        }
    }


    //Use to update data when a record has been updated.  Does not update expiration.
    protected setCacheData(data: T) {
        this.cache.data = data
    }


    reloadData(): Observable<CacheObject<T>> {
        this.reloadInProgress = true;
        this.reloadGuard += 1;

        // guard to prevent infinite recursion due to recursion in finalize
        if (this.reloadGuard > 15) {
            throw new Error("Too many reloads");
        }
        return this.http.get<T>(this.getApiUrl()).pipe(
            switchMap((data) => {
                this.reloadInProgress = false;
                this.reloadGuard = 0;
                this.cache = { data: data, timestamp: new Date() };
                this.broadcastService.cachingApiServiceReload(this.broadcastKey, this.cache)
                this.reloadComplete.next(this.cache)
                return of({...this.cache});
            }),
            catchError(err => {
                if (err.name === "TokenRefreshError") {
                    //on token refresh failure, don't retry.  Causes an infinite loop
                    this.reloadInProgress = false;
                }

                return throwError(() => err);
                
            }),
            finalize(() => {
                if (this.reloadInProgress) {
                    // if the reload is still in progress, then it's likely the observable was cancelled.  We need to go ahead
                    // and reload to trigger any reloadComplete subscribers.
                    this.reloadData().subscribe(() => { })
                }
            })
        )
    }

    expireCache() {
        if (this.cache) {
            this.cache.timestamp = new Date(0);
        }
    }

    abstract getApiUrl(): string;


}